import type { ClientProjectStatus, ProjectHealth } from "../../../store/useClientProjectsStore";

// Small pieces shared by the Projects screen and its three modals, kept in one
// place so a status chip looks identical wherever it appears.

/** Material Symbols icon — the icon set the approved design uses. */
export const MIcon = ({ name, className = "" }: { name: string; className?: string }) => (
  <span className={`material-symbols-outlined ${className}`} aria-hidden="true">
    {name}
  </span>
);

export const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

export const fmtShort = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—";

export const money = (n: number, currency = "USD") => {
  const value = Number(n || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString()}`;
  }
};

// Status is the project's formal state. Awaiting Client is the one the client
// can act on, so it reads as an alert rather than as neutral information.
const STATUS_TONE: Record<ClientProjectStatus, string> = {
  "Not Started": "bg-surface-container text-on-surface-variant",
  "In Progress": "bg-primary/10 text-primary",
  "Awaiting Client": "bg-error-container text-error",
  "On Hold": "bg-surface-container text-on-surface-variant",
  Completed: "bg-tertiary-container text-tertiary",
  Cancelled: "bg-surface-container text-on-surface-variant",
};

export const StatusChip = ({ status }: { status: ClientProjectStatus }) => (
  <span
    className={`px-2.5 py-1 rounded-full text-xs font-bold inline-flex items-center gap-1 whitespace-nowrap ${
      STATUS_TONE[status] || STATUS_TONE["Not Started"]
    }`}
  >
    <span className="w-1.5 h-1.5 rounded-full bg-current" />
    {status}
  </span>
);

// Health is a different question from Status: how delivery is going, rather
// than what state the project is formally in.
const HEALTH_TONE: Record<ProjectHealth, string> = {
  "On Track": "bg-tertiary-container text-tertiary",
  "Attention Required": "bg-amber-100 text-amber-700",
  "At Risk": "bg-error-container text-error",
  Delayed: "bg-error-container text-error",
  "Awaiting Client": "bg-amber-100 text-amber-700",
  "On Hold": "bg-surface-container text-on-surface-variant",
  Completed: "bg-tertiary-container text-tertiary",
};

export const HealthChip = ({ health }: { health: ProjectHealth }) => (
  <span
    className={`px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
      HEALTH_TONE[health] || HEALTH_TONE["On Track"]
    }`}
  >
    {health}
  </span>
);

// The icon that sits beside a stage name, chosen from what the stage is about.
export const stageIcon = (stage: string) => {
  const s = (stage || "").toLowerCase();
  if (/complete/.test(s)) return "check_circle";
  if (/review|approval/.test(s)) return "rate_review";
  if (/develop|build|release/.test(s)) return "terminal";
  if (/test|qa/.test(s)) return "bug_report";
  if (/design|concept|creative|ui/.test(s)) return "palette";
  if (/plan|requirement|discovery|brief|strategy/.test(s)) return "lightbulb";
  if (/deliver|handover|launch|render/.test(s)) return "inventory_2";
  if (/change|revision|optimi/.test(s)) return "tune";
  if (/campaign|running/.test(s)) return "campaign";
  return "radio_button_checked";
};

// What an invoice means to the client. Deliberately not our internal billing
// statuses: this list is about what the client owes and by when.
const INVOICE_TONE: Record<string, string> = {
  Upcoming: "bg-surface-container text-on-surface-variant",
  Due: "bg-amber-100 text-amber-700",
  Overdue: "bg-error-container text-error",
  "Payment Processing": "bg-primary/10 text-primary",
  "Partially Paid": "bg-amber-100 text-amber-700",
  Paid: "bg-tertiary-container text-tertiary",
  Cancelled: "bg-surface-container text-on-surface-variant",
};

export const InvoiceChip = ({ status }: { status: string }) => (
  <span
    className={`px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap ${
      INVOICE_TONE[status] || INVOICE_TONE.Upcoming
    }`}
  >
    {status}
  </span>
);

// Something that has already happened reads differently from something that
// might. The badge is what keeps the two apart at a glance, so a client is
// never alarmed by a risk or relaxed by a fact.
export const CertaintyBadge = ({ certainty }: { certainty: "confirmed" | "potential" }) => (
  <span
    className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${
      certainty === "confirmed"
        ? "bg-error-container text-error"
        : "bg-amber-100 text-amber-700"
    }`}
  >
    {certainty === "confirmed" ? "Needs attention" : "Possible risk"}
  </span>
);

export const attentionIcon = (kind: string) => {
  switch (kind) {
    case "issue": return "error";
    case "payment": return "credit_card_off";
    case "action": return "schedule";
    case "risk": return "warning";
    default: return "flag";
  }
};

export const PRIORITY_TONE: Record<string, string> = {
  Blocking: "bg-error-container text-error",
  Required: "bg-amber-100 text-amber-700",
  Upcoming: "bg-surface-container text-on-surface-variant",
};

export const actionIcon = (type: string) => {
  switch (type) {
    case "Review": return "rate_review";
    case "Provide Feedback": return "comment";
    case "Approve": return "task_alt";
    case "Provide Information": return "help";
    case "Upload Document": return "upload_file";
    case "Make Payment": return "payments";
    default: return "flag";
  }
};

/** A modal shell — same chrome for lifecycle, details and the AI summary. */
export const ModalShell = ({
  open,
  onClose,
  icon,
  title,
  subtitle,
  width = "max-w-4xl",
  children,
}: {
  open: boolean;
  onClose: () => void;
  icon: string;
  title: string;
  subtitle?: string;
  width?: string;
  children: React.ReactNode;
}) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/45 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className={`bg-surface-container-lowest w-full ${width} rounded-xl shadow-2xl my-auto max-h-[92vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-lg border-b border-outline-variant">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <MIcon name={icon} className="text-[18px]" />
            </div>
            <div className="min-w-0">
              <h2 className="text-body-md font-bold text-on-surface truncate">{title}</h2>
              {subtitle && <p className="text-body-sm text-on-surface-variant mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container-low transition-colors flex-shrink-0"
          >
            <MIcon name="close" className="text-[20px]" />
          </button>
        </div>
        <div className="p-lg overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

/** Honest empty state — never a fake row, never a spinner that never ends. */
export const EmptyState = ({
  icon = "inbox",
  title,
  detail,
}: {
  icon?: string;
  title: string;
  detail?: string;
}) => (
  <div className="py-xl px-lg text-center">
    <div className="w-11 h-11 rounded-xl bg-surface-container-low text-on-surface-variant grid place-items-center mx-auto mb-3">
      <MIcon name={icon} className="text-[20px]" />
    </div>
    <p className="text-body-md font-semibold text-on-surface">{title}</p>
    {detail && <p className="text-body-sm text-on-surface-variant mt-1 max-w-sm mx-auto">{detail}</p>}
  </div>
);
